/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Alert, Button, Checkbox, Toast } from '@/components/ui'
import { useApi } from '@/hooks'
import useToast from '@/hooks/useToast'
import logger from '@/utils/logger'

import {
  ConfirmDisableModal,
  PermissionsEditor,
  UnsavedChangesNote,
  applyCap,
  buildUsage,
  dashboardsLabel,
  disabledInUse,
  registeredInsights,
  isAppGrant,
  normaliseValue,
  useAccessCap,
  usePermissionsForm,
  useSaveConfirm,
  type AppGrant,
} from '../permissions'
import { AssetGroupsDropdown } from '../permissions/AssetGroupsDropdown'
import { CustomerDropdown } from '../permissions/CustomerDropdown'
import type { AdminUserDetail, UserAccessBody } from '../types'

const log = logger({ category: 'AdminUserDetailPage' })

interface Props {
  user: AdminUserDetail
  uuid: string
  isSelf: boolean
}

interface UserPermissionsForm {
  accessEnabled: boolean
  support: boolean
  companyId: number | undefined
  /** Fresh company ids of the asset groups the user is limited to; empty = every asset group of the company. */
  assetGroups: number[]
  /**
   * The stored ['*'] wildcard: every asset group of every customer, which is Cooltrax-staff
   * scope, not "all of this company's". Preserved on save until the admin picks groups or
   * changes the company, either of which narrows the user to the company.
   */
  allCustomers: boolean
  grants: AppGrant[]
  insights: string[]
}

const normalise = (v: UserPermissionsForm) => ({
  accessEnabled: v.accessEnabled,
  support: v.support,
  companyId: v.companyId ?? null,
  assetGroups: [...v.assetGroups].sort((a, b) => a - b),
  allCustomers: v.allCustomers,
  ...normaliseValue({ grants: v.grants, insights: v.insights }),
})

/** The stored scope as editor state: the ids, or nothing for [] and for the ['*'] wildcard. */
const assetGroupIdsOf = (scope: AdminUserDetail['assetGroups']): number[] =>
  scope.flatMap((entry) => (entry === '*' ? [] : [entry.id]))

/** What the stored scope says, for the moment the picker has nothing loaded yet. */
const assetGroupsText = (scope: AdminUserDetail['assetGroups']): string => {
  if (scope.includes('*')) return 'Every customer (Cooltrax staff scope)'
  if (scope.length === 0) return 'All asset groups of the company'
  return scope.flatMap((e) => (e === '*' ? [] : [e.name ?? `#${String(e.id)}`])).join(', ')
}

/**
 * Company, access, support, and the apps + insights editor — one form, one save.
 * The company's profile, narrowed by the asset groups', caps what can be ticked;
 * changing the company or the groups reapplies it.
 */
export const PermissionsCard: React.FC<Props> = ({ user, uuid, isSelf }) => {
  const { api } = useApi()
  const { toast, show, showToast, hideToast } = useToast()
  const baseline = useMemo<UserPermissionsForm>(
    () => ({
      accessEnabled: !user.inactive,
      support: user.support,
      companyId: user.companyId ?? undefined,
      assetGroups: assetGroupIdsOf(user.assetGroups),
      allCustomers: user.assetGroups.includes('*'),
      grants: user.permissions.filter(isAppGrant),
      insights: registeredInsights(user.insights),
    }),
    [user]
  )
  const { value, setValue, isDirty, reset, commit } = usePermissionsForm<UserPermissionsForm>({ baseline, normalise })
  const form = value ?? baseline
  const usage = useMemo(() => buildUsage([{ uid: user.uid, dashboards: user.dashboards }]), [user])
  // The company's profile narrowed by the picked asset groups' (MEGA-776) — what the token will apply.
  const scope = useMemo<(number | '*')[]>(
    () => (form.allCustomers ? ['*'] : form.assetGroups),
    [form.allCustomers, form.assetGroups]
  )
  const { cap } = useAccessCap(form.companyId, scope)
  const { pending, request, confirm, cancel } = useSaveConfirm()
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [capNote, setCapNote] = useState('')

  // A company that has switched something off takes it away here too, so the admin
  // sees exactly what will be saved rather than a ticked-but-disabled box.
  useEffect(() => {
    if (!cap) return
    setValue((previous) => {
      const { value: capped, removed } = applyCap({ grants: previous.grants, insights: previous.insights }, cap)
      if (removed.length === 0) return previous
      setCapNote(
        `${String(removed.length)} ${removed.length === 1 ? 'permission was' : 'permissions were'} unchecked because ${cap.companyName} does not enable ${removed.length === 1 ? 'it' : 'them'}: ${removed.join(', ')}`
      )
      return { ...previous, ...capped }
    })
  }, [cap, setValue])

  const save = useCallback(async () => {
    setIsSaving(true)
    setError('')
    try {
      const response = await api.put<{ uid: string }, UserAccessBody>({
        path: `/app/admin/users/${encodeURIComponent(uuid)}/access`,
        origin: 'AdminUserDetailPage#saveAccess',
        body: {
          inactive: !form.accessEnabled,
          support: form.support,
          permissions: form.grants,
          insights: form.insights,
          customerId: form.companyId ?? null,
          // [] = every asset group of the company; ['*'] is the Cooltrax every-customer wildcard,
          // only ever carried forward from what was stored.
          assetGroups: form.allCustomers ? ['*'] : form.assetGroups,
        },
      })
      if (response.error) {
        setError(response.message || 'Could not save')
        return
      }
      commit(form)
      setCapNote('')
      showToast({
        header: 'Permissions saved',
        body: 'Changes apply at the user’s next sign-in or token refresh.',
        position: 'end',
        success: true,
      })
    } catch (ex) {
      log.error(ex)
      setError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }, [api, commit, form, showToast, uuid])

  const handleSave = () => {
    const disabled = disabledInUse(
      { grants: baseline.grants, insights: baseline.insights },
      { grants: form.grants, insights: form.insights },
      usage
    )
    request(disabled, () => void save())
  }

  const handleCancel = () => {
    reset()
    setCapNote('')
    setError('')
  }

  const header = (
    <>
      <div className="mt-2">
        {/* CustomerDropdown renders its own "Company" label. */}
        <div className="flex items-center gap-2">
          <div className="grow">
            <CustomerDropdown
              value={form.companyId}
              // A different company has different asset groups, so the selection starts over.
              onChange={(companyId) =>
                setValue((p) => ({
                  ...p,
                  companyId,
                  assetGroups: companyId === p.companyId ? p.assetGroups : [],
                  allCustomers: companyId === p.companyId ? p.allCustomers : false,
                }))
              }
            />
          </div>
          {form.companyId !== undefined && (
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setValue((p) => ({ ...p, companyId: undefined, assetGroups: [], allCustomers: false }))}
            >
              Clear
            </button>
          )}
        </div>
        <span className="label-text-alt mt-1 block text-base-content/60">
          The user sees this company and everything under it. No company means no data (staff excepted).
        </span>
        <div className="mt-2">
          <AssetGroupsDropdown
            customerId={form.companyId}
            value={form.assetGroups}
            onChange={(assetGroups) => setValue((p) => ({ ...p, assetGroups, allCustomers: false }))}
          />
          {form.companyId !== undefined && form.companyId === (user.companyId ?? undefined) && (
            <span className="label-text-alt mt-1 block text-base-content/60" data-testid="asset-groups-current">
              Currently: {assetGroupsText(user.assetGroups)}
            </span>
          )}
        </div>
        {user.freshCompanyName && (
          <span className="label-text-alt mt-1 block text-base-content/60" data-testid="fresh-company">
            Fresh currently lists them under <span className="font-medium">{user.freshCompanyName}</span>.
            {user.freshCompanyId !== null && form.companyId !== undefined && user.freshCompanyId !== form.companyId && (
              <span> Clearing the company above adopts this at their next sign-in.</span>
            )}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <Checkbox
          id="access-enabled"
          data-testid="access-enabled"
          label="Access enabled"
          size="md"
          checked={form.accessEnabled}
          disabled={isSelf}
          onChange={(checked) => setValue((p) => ({ ...p, accessEnabled: checked }))}
          trailing={
            isSelf ? (
              <span className="text-xs text-base-content/60">You cannot deactivate your own access.</span>
            ) : undefined
          }
        />
        <Checkbox
          id="access-support"
          data-testid="access-support"
          label="Support"
          size="md"
          checked={form.support}
          onChange={(checked) => setValue((p) => ({ ...p, support: checked }))}
          trailing={
            <span className="text-xs text-base-content/60">
              Cooltrax support: grants staff, preview and impersonate
            </span>
          }
        />
      </div>
      {capNote && (
        <div data-testid="cap-applied-note">
          <Alert type="info" message={capNote} />
        </div>
      )}
    </>
  )

  return (
    <section className="card bg-base-100 shadow">
      <div className="card-body">
        <h2 className="card-title text-base">Permissions</h2>
        <p className="text-sm text-base-content/70">
          This profile is the user&rsquo;s access grant — Auth0 only signs them in. Changes take effect at their next
          sign-in or token refresh.
        </p>

        <PermissionsEditor
          value={{ grants: form.grants, insights: form.insights }}
          onChange={(next) => setValue((p) => ({ ...p, ...next }))}
          cap={cap}
          usage={usage}
          inUseLabel={dashboardsLabel}
          disabled={isSaving}
          header={header}
          sectionsReplacedBy={
            form.support ? (
              <p className="mt-3 text-sm text-base-content/70" data-testid="support-unrestricted">
                Support users have every app and insight; the lists below do not apply.
              </p>
            ) : undefined
          }
        />

        {user.provisionedBy && <p className="mt-2 text-xs text-base-content/50">Provisioned by {user.provisionedBy}</p>}

        {error && <Alert message={error} type="error" />}

        <div className="card-actions mt-4 justify-end">
          <Button
            handleClick={handleCancel}
            isDisabled={!isDirty || isSaving}
            variation="ghost"
            ariaLabel="Cancel permission changes"
          >
            Cancel
          </Button>
          <Button handleClick={handleSave} isDisabled={!isDirty || isSaving} variation="primary">
            {isSaving ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Saving…
              </>
            ) : (
              'Save permissions'
            )}
          </Button>
        </div>
        <UnsavedChangesNote show={isDirty} />

        <ConfirmDisableModal
          disabled={pending}
          usage={usage}
          inUseLabel={dashboardsLabel}
          isSaving={isSaving}
          onConfirm={confirm}
          onCancel={cancel}
        />
        <Toast
          show={show}
          onClose={hideToast}
          header={toast.header}
          body={toast.body}
          position={toast.position}
          success={toast.success}
          info={toast.info}
          warning={toast.warning}
        />
      </div>
    </section>
  )
}

export default PermissionsCard
